import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import type { FluidPropertiesPipelineInputs } from "../types";
import { calculateGasSeparationEfficiencies } from "../../gasSeparationCalculations";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";

// Natural and intake gas separation, and the fluid properties at the intake
// once natural separation is applied.
export const calculateIntakeGasSeparation = (
  inputs: FluidPropertiesPipelineInputs,
  prev: {
    wellFlowingPressureAtIntakePSIA: number;
    downholeIntakeTemperatureF: number;
    fluidPropertiesAtSealsContext: FluidPropertiesContext;
  }
) => {
  const {
    basicData,
    correlations,
    desiredFlowRates,
    bubblePointPressurePSIA,
    condensateApi,
    dewPointPressurePSIA,
    customFluidProperties,
    customViscosityProperties,
    fluidPropertiesOverrides,
    orderedIntakesInfo,
    primaryIntakeOuterDiameterInches,
    primaryIntakePortSquareInches,
    bottomOfIntakesMDFt,
    intakePackerInstalled,
    naturalGasSeparationOverrideType,
    intakeGasSeparationOverrideType,
    naturalOverrideGasSeparationPercent,
    intakeOverrideGasSeparationPercent,
    intakeCrossSectionFt2,
    effectiveCasingInteriorDiameterAtIntakeIn
  } = inputs;
  const {
    wellFlowingPressureAtIntakePSIA,
    downholeIntakeTemperatureF,
    fluidPropertiesAtSealsContext
  } = prev;

  // This is just used for finding the natural gas sep
  const fluidPropertiesAtIntakeWithoutNaturalSeparationContext =
    getFluidPropertiesContext(
      basicData,
      correlations,
      wellFlowingPressureAtIntakePSIA,
      downholeIntakeTemperatureF,
      effectiveCasingInteriorDiameterAtIntakeIn,
      desiredFlowRates,
      fluidPropertiesAtSealsContext.flowRatesInSitu,
      fluidPropertiesAtSealsContext.flowRatePrerequisites,
      customFluidProperties,
      customViscosityProperties,
      bubblePointPressurePSIA,
      dewPointPressurePSIA,
      intakeCrossSectionFt2,
      0,
      fluidPropertiesOverrides,
      condensateApi,
      true
    );

  // Calculate gas separation efficiencies
  // Note: For intake gas separation, we use the "without natural separation" context
  // since we're still calculating the separations and don't have the final context yet
  const gasSeparationEfficiencies = calculateGasSeparationEfficiencies({
    basicData: {
      topCasingInteriorDiameterIn: basicData.topCasingInteriorDiameterIn,
      bottomCasingInteriorDiameterIn: basicData.bottomCasingInteriorDiameterIn,
      topCasingLengthFt: basicData.topCasingLengthFt
    },
    correlations,
    orderedIntakesInfo,
    primaryIntakeOuterDiameterInches,
    primaryIntakePortSquareInches,
    bottomOfIntakesMDFt,
    fluidPropertiesAtIntakeWithoutNaturalSeparationContext,
    fluidPropertiesAtIntakeContext:
      fluidPropertiesAtIntakeWithoutNaturalSeparationContext,
    desiredFlowRates,
    naturalGasSeparationOverrideType,
    naturalOverrideGasSeparationPercent,
    intakeGasSeparationOverrideType,
    intakeOverrideGasSeparationPercent,
    intakePackerInstalled
  });

  // Fluid properties at intake with natural gas separation applied
  const fluidPropertiesAtIntakeContext = getFluidPropertiesContext(
    basicData,
    correlations,
    wellFlowingPressureAtIntakePSIA,
    downholeIntakeTemperatureF,
    effectiveCasingInteriorDiameterAtIntakeIn,
    desiredFlowRates,
    fluidPropertiesAtSealsContext.flowRatesInSitu,
    fluidPropertiesAtSealsContext.flowRatePrerequisites,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    intakeCrossSectionFt2,
    gasSeparationEfficiencies.naturalGasSeparationEfficiencyRatio,
    fluidPropertiesOverrides,
    condensateApi,
    true
  );
  return {
    gasSeparationEfficiencies,
    fluidPropertiesAtIntakeContext
  };
};
